import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { eventTicketEntries } from "@/lib/eventDiscovery";

export function useEventClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

/** Published tier totals are public; unlike tickets, these never expose buyer identities. */
export function useEventActivity(eventIds: string[]) {
  const key = [...new Set(eventIds)].sort().join(",");
  const qc = useQueryClient();
  useEffect(() => {
    const channel = supabase.channel(`event-discovery-${crypto.randomUUID()}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "events" }, () => {
        void qc.invalidateQueries({ queryKey: ["events"] });
        void qc.invalidateQueries({ queryKey: ["public-events"] });
      }).subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [key, qc]);

  const query = useQuery({
    queryKey: ["event-discovery-tickets", key],
    enabled: !!key,
    refetchInterval: 15_000,
    queryFn: async () => {
      const ids = key.split(",");
      const { data, error } = await supabase.from("ticket_types")
        .select("event_id, quantity_sold, admits_per_ticket")
        .in("event_id", ids);
      if (error) throw error;
      const totals: Record<string, number> = {};
      for (const id of ids) totals[id] = eventTicketEntries((data ?? []).filter((tier) => tier.event_id === id));
      return totals;
    },
  });
  return { ...query, ticketEntries: query.data ?? {} };
}